import { Selectable } from 'kysely'
import { PressReleases, PrFilterKeywords } from './db.js'

type WarrantEntityWithNames = Record<
    'instrumentName' | 'companyName' | 'name' | 'instrumentSymbol',
    string | null | undefined
>

const ensureWhitespace = (str?: string | null) => {
    if (!str) return ''

    const match = str.match(/((?<=TO)[0-9])/)

    if (!match) return str

    return str.slice(0, match.index) + ' ' + str.slice(match.index)
}

const ensureTOPrefix = (str: string | null) => {
    if (!str) return ''

    return str.replace(/^([0-9])/, 'TO $1')
}

type INormalizedWarrantName = {
    (warrant: WarrantEntityWithNames, displayType?: 'full' | 'short'): string
    (warrant: Omit<WarrantEntityWithNames, 'instrumentSymbol'>, displayType?: 'full'): string
}

const normalizedName = (name?: string | null) => name?.replace(/ \(publ\)/, '')

export const normalizedWarrantName = ((warrant: WarrantEntityWithNames, displayType: 'full' | 'short' = 'full') => {
    if (!warrant) return ''

    if (displayType === 'short') {
        return ensureWhitespace(warrant.instrumentSymbol) || ensureTOPrefix(ensureWhitespace(warrant.name))
    }

    if (warrant.instrumentName) return ensureWhitespace(warrant.instrumentName)

    return `${normalizedName(warrant.companyName)} ${ensureTOPrefix(ensureWhitespace(warrant.name))}`
}) as INormalizedWarrantName

export const shouldKeepItemByFieldMatch = <I extends Pick<Selectable<PressReleases>, 'title'>, T extends keyof I>(
    filters: Pick<Selectable<PrFilterKeywords>, 'field' | 'negation' | 'phrase'>[],
    item: I,
    field: T,
    restList: Array<I>,
) => {
    const filtersForField = filters.filter((f) => !field || f.field === field)
    const positivePhrases = filtersForField.filter((f) => !f.negation).map((f) => f.phrase)
    const negativePhrases = filtersForField.filter((f) => f.negation).map((f) => f.phrase)

    {
        const match = negativePhrases.find((phrase) =>
            (item[field] as string | null)?.toLowerCase().includes(phrase.toLowerCase()),
        )

        if (match) {
            console.log(`Press release should be filtered due to match on the negative phrase ${match}`, {
                field,
                item,
            })
            return false
        }
    }
    {
        const match = positivePhrases.some((phrase) =>
            (item[field] as string | null)?.toLowerCase().includes(phrase.toLowerCase()),
        )
        if (match) {
            console.log(`Press release should not be filtered due to match on the positive phrase ${match}`, {
                field,
                item,
            })
            return true
        }
    }

    console.log('Press release should be filtered, but to be put in rest list to be checked for further criteria.')

    // default: save the item in the rest list, and remove it from the current list
    restList.push(item)
    return false
}
